import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { fetchAssetDownloadUrl, fetchAssets } from '@/api/assets';
import { ApiError } from '@/api/client';
import { fetchGame, fetchGames } from '@/api/library';
import { Button } from '@/components/ui/Button';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { SearchInput } from '@/components/ui/SearchInput';
import { Spinner } from '@/components/ui/Spinner';
import { useI18n } from '@/i18n';
import { displayTitle } from '@/lib/displayTitle';
import { gamePoster, gamePosterFallback } from '@/lib/gameArt';
import type { AssetPack, HubGame } from '@/types';

type AssetCategory = 'live' | 'slot';
type FileType = 'png' | 'jpg' | 'psd' | 'gif' | 'pdf' | 'zip' | 'svg' | 'eps' | 'others';
type Orientation = 'landscape' | 'portrait' | 'square';

const FILE_TYPES: Array<{ value: FileType; label: string }> = [
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'psd', label: 'PSD' },
  { value: 'gif', label: 'GIF' },
  { value: 'pdf', label: 'PDF' },
  { value: 'zip', label: 'ZIP' },
  { value: 'svg', label: 'SVG' },
  { value: 'eps', label: 'EPS' },
  { value: 'others', label: 'Others' },
];

const ORIENTATIONS: Array<{ value: Orientation; label: string }> = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'square', label: 'Square' },
];

function alphaSort(games: HubGame[]): HubGame[] {
  return [...games].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
}

function assetSearchText(asset: AssetPack): string {
  return [
    asset.title,
    asset.type,
    asset.category,
    asset.description,
    asset.fileName,
    asset.extension,
    asset.mimeType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function assetFileType(asset: AssetPack): FileType {
  const text = assetSearchText(asset);
  if (/\b(jpe?g)\b/.test(text)) return 'jpg';
  const match = FILE_TYPES.find(({ value }) => value !== 'jpg' && value !== 'others' && new RegExp(`\\b${value}\\b`).test(text));
  return match?.value ?? 'others';
}

function assetOrientation(asset: AssetPack): Orientation | null {
  if (asset.width && asset.height) {
    if (Math.abs(asset.width - asset.height) / Math.max(asset.width, asset.height) < 0.08) return 'square';
    return asset.width > asset.height ? 'landscape' : 'portrait';
  }

  const text = assetSearchText(asset);
  if (/\b(landscape|horizontal|wide)\b/.test(text)) return 'landscape';
  if (/\b(portrait|vertical)\b/.test(text)) return 'portrait';
  if (/\b(square|1x1|1:1)\b/.test(text)) return 'square';
  return null;
}

function formatSize(bytes?: number): string | null {
  if (!bytes || bytes < 1) return null;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.5 6.5h6l1.8 2h9.2v9.25a1.75 1.75 0 0 1-1.75 1.75H5.25a1.75 1.75 0 0 1-1.75-1.75V6.5Z" />
      <path d="M3.5 8.5h17" />
    </svg>
  );
}

export function AssetsPage() {
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const gameId = searchParams.get('gameId') ?? undefined;

  const [activeCategory, setActiveCategory] = useState<AssetCategory>('live');
  const [games, setGames] = useState<Record<AssetCategory, HubGame[]>>({ live: [], slot: [] });
  const [gamesLoading, setGamesLoading] = useState(true);
  const [assets, setAssets] = useState<AssetPack[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [fileTypes, setFileTypes] = useState<Set<FileType>>(new Set());
  const [orientations, setOrientations] = useState<Set<Orientation>>(new Set());
  const [gameQuery, setGameQuery] = useState('');

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setGamesLoading(true);
    setError(null);

    Promise.allSettled([
      fetchGames(accessToken, { category: 'live', limit: 200 }, sessionId),
      fetchGames(accessToken, { category: 'slot', limit: 200 }, sessionId),
      gameId ? fetchGame(gameId, accessToken, sessionId) : Promise.resolve(null),
    ])
      .then(([liveResult, slotResult, requestedResult]) => {
        if (cancelled) return;
        const next = {
          live: liveResult.status === 'fulfilled' ? liveResult.value : [],
          slot: slotResult.status === 'fulfilled' ? slotResult.value : [],
        };

        if (requestedResult.status === 'fulfilled' && requestedResult.value) {
          const requested = requestedResult.value;
          const category: AssetCategory = requested.category.toLowerCase().includes('live')
            ? 'live'
            : 'slot';
          if (!next[category].some((game) => game.id === requested.id)) {
            next[category] = [...next[category], requested];
          }
          setActiveCategory(category);
        }

        next.live = alphaSort(next.live);
        next.slot = alphaSort(next.slot);
        setGames(next);

        const allGames = [...next.live, ...next.slot];
        const current = gameId ? allGames.find((game) => game.id === gameId) : null;
        const initial = current ?? next.live[0] ?? next.slot[0];
        if (initial && initial.id !== gameId) {
          const params = new URLSearchParams(searchParams);
          params.set('gameId', initial.id);
          setSearchParams(params, { replace: true });
          setActiveCategory(initial.category.toLowerCase().includes('live') ? 'live' : 'slot');
        }
      })
      .catch(() => setError(t('assets.error')))
      .finally(() => {
        if (!cancelled) setGamesLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Game lists only need refreshing when the session or locale changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, sessionId, locale]);

  useEffect(() => {
    if (!accessToken || !gameId) {
      setAssets([]);
      setAssetsLoading(false);
      return;
    }

    let cancelled = false;
    setAssetsLoading(true);
    setError(null);
    setSelectedAssetId(null);
    fetchAssets(accessToken, { gameId, limit: 100 }, sessionId)
      .then((items) => {
        if (!cancelled) setAssets(items);
      })
      .catch(() => {
        if (!cancelled) setError(t('assets.error'));
      })
      .finally(() => {
        if (!cancelled) setAssetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, sessionId, gameId, locale, t]);

  const selectedGame = useMemo(
    () => [...games.live, ...games.slot].find((game) => game.id === gameId) ?? null,
    [games, gameId],
  );

  const filteredAssets = useMemo(
    () =>
      assets.filter((asset) => {
        const fileMatch = fileTypes.size === 0 || fileTypes.has(assetFileType(asset));
        const orientation = assetOrientation(asset);
        const orientationMatch =
          orientations.size === 0 || (orientation !== null && orientations.has(orientation));
        return fileMatch && orientationMatch;
      }),
    [assets, fileTypes, orientations],
  );

  const selectedAsset =
    filteredAssets.find((asset) => asset.id === selectedAssetId) ??
    assets.find((asset) => asset.id === selectedAssetId) ??
    null;

  const listedGames = useMemo(() => {
    const query = gameQuery.trim().toLocaleLowerCase(locale);
    const pool = query ? [...games.live, ...games.slot] : games[activeCategory];
    const filtered = query
      ? pool.filter((game) => {
          const title = displayTitle(game.title).toLocaleLowerCase(locale);
          return title.includes(query) || game.title.toLocaleLowerCase(locale).includes(query);
        })
      : pool;
    return alphaSort(filtered);
  }, [activeCategory, gameQuery, games, locale]);

  const gamesByLetter = useMemo(() => {
    const groups = new Map<string, HubGame[]>();
    listedGames.forEach((game) => {
      const title = displayTitle(game.title);
      const letter = title.trim().charAt(0).toLocaleUpperCase(locale) || '#';
      groups.set(letter, [...(groups.get(letter) ?? []), game]);
    });
    return [...groups.entries()];
  }, [listedGames, locale]);

  async function handleDownload(asset: AssetPack) {
    if (!accessToken) return;
    setDownloadingId(asset.id);
    try {
      const { url } = await fetchAssetDownloadUrl(
        asset.id,
        accessToken,
        asset.category,
        sessionId,
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('assets.downloadFailed'));
    } finally {
      setDownloadingId(null);
    }
  }

  function selectGame(game: HubGame) {
    const params = new URLSearchParams(searchParams);
    params.set('gameId', game.id);
    setSearchParams(params);
    setActiveCategory(game.category.toLowerCase().includes('live') ? 'live' : 'slot');
  }

  function selectCategory(category: AssetCategory) {
    setActiveCategory(category);
    const firstGame = games[category][0];
    if (firstGame) selectGame(firstGame);
  }

  function toggleFilter<T extends string>(value: T, setter: React.Dispatch<React.SetStateAction<Set<T>>>) {
    setter((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  const [leadBefore, leadAfter] = t('assets.lead').split('{link}');
  const fallbackPreview = selectedGame ? gamePoster(selectedGame) : '/banners/casinogenel.webp';

  return (
    <section className="assets-page site-pad-x">
      <header className="page-header reveal is-in">
        <p className="eyebrow">{t('assets.eyebrow')}</p>
        <h1 className="page-title display">{t('assets.title')}</h1>
        <p className="page-lead">
          {leadBefore}
          <Link to="/guidelines">{t('assets.leadLink')}</Link>
          {leadAfter}
        </p>
      </header>

      {gamesLoading ? (
        <div className="page-loading">
          <Spinner label={t('assets.loading')} />
        </div>
      ) : null}

      {error ? <ErrorMessage message={error} /> : null}

      {!gamesLoading ? (
        <div className="asset-browser reveal is-in reveal-delay-1">
          <aside className="asset-browser__library" aria-label="Game library">
            <div className="asset-browser__category-tabs">
              {(['live', 'slot'] as const).map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`asset-browser__category-tab${activeCategory === category ? ' is-active' : ''}`}
                  onClick={() => selectCategory(category)}
                >
                  {category === 'live' ? 'Live' : 'Slot'}
                </button>
              ))}
            </div>

            <div className="asset-browser__search">
              <SearchInput
                id="asset-game-search"
                value={gameQuery}
                onChange={setGameQuery}
                placeholder={t('assets.search')}
              />
            </div>

            <div className="asset-browser__library-heading">
              <span>{activeCategory === 'live' ? 'Live' : 'Slots'}</span>
              <span>{listedGames.length}</span>
            </div>

            <div className="asset-browser__game-tree">
              {gamesByLetter.map(([letter, letterGames]) => (
                <div key={letter} className="asset-browser__letter-group">
                  <div className="asset-browser__letter">{letter}</div>
                  {letterGames.map((game) => (
                    <button
                      key={game.id}
                      type="button"
                      className={`asset-browser__game${game.id === gameId ? ' is-active' : ''}`}
                      onClick={() => selectGame(game)}
                    >
                      <span className="asset-browser__folder-icon"><FolderIcon /></span>
                      <span>{displayTitle(game.title)}</span>
                    </button>
                  ))}
                </div>
              ))}
              {listedGames.length === 0 ? (
                <p className="asset-browser__sidebar-empty">
                  {gameQuery.trim() ? t('assets.searchEmpty') : t('assets.emptyGeneral')}
                </p>
              ) : null}
            </div>
          </aside>

          <main className="asset-browser__content">
            <div className="asset-browser__toolbar">
              <nav className="asset-browser__crumbs" aria-label="Asset location">
                <span>{activeCategory === 'live' ? 'Live' : 'Slots'}</span>
                {selectedGame ? (
                  <>
                    <span aria-hidden="true">/</span>
                    <strong>{selectedGame.title}</strong>
                  </>
                ) : null}
              </nav>
              <span className="asset-browser__count">
                {filteredAssets.length} {filteredAssets.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            {assetsLoading ? (
              <div className="asset-browser__loading">
                <Spinner label={t('assets.loading')} />
              </div>
            ) : null}

            {!assetsLoading && filteredAssets.length > 0 ? (
              <div className="asset-folder-grid">
                {filteredAssets.map((asset, index) => (
                  <button
                    key={asset.id}
                    type="button"
                    className={`asset-folder${selectedAssetId === asset.id ? ' is-selected' : ''}`}
                    onClick={() => setSelectedAssetId(asset.id)}
                    onDoubleClick={() => handleDownload(asset)}
                    style={{ '--asset-index': index } as React.CSSProperties}
                  >
                    <span className="asset-folder__tab" aria-hidden="true" />
                    <span className="asset-folder__preview">
                      <img
                        src={asset.thumbnailUrl ?? asset.previewUrl ?? fallbackPreview}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = selectedGame
                            ? gamePosterFallback(selectedGame)
                            : '/banners/casinogenel.webp';
                        }}
                      />
                      <span className="asset-folder__veil" />
                      <span className="asset-folder__format">{assetFileType(asset).toUpperCase()}</span>
                    </span>
                    <span className="asset-folder__name">{asset.title}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {!assetsLoading && filteredAssets.length === 0 ? (
              <div className="asset-browser__empty">
                <FolderIcon />
                <h2>{assets.length > 0 ? 'No matching assets' : t('assets.emptyTitle')}</h2>
                <p>
                  {assets.length > 0
                    ? 'Clear the filters to see every folder.'
                    : gameId
                      ? t('assets.emptyForGame')
                      : t('assets.emptyGeneral')}
                </p>
              </div>
            ) : null}

            {selectedAsset ? (
              <div className="asset-browser__selection">
                <div>
                  <span className="asset-browser__selection-label">
                    {selectedAsset.type ?? t('assets.pack')}
                  </span>
                  <strong>{selectedAsset.title}</strong>
                  <span>
                    {[formatSize(selectedAsset.fileSize), selectedAsset.updatedAt]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <Button
                  variant="glass"
                  disabled={downloadingId === selectedAsset.id}
                  onClick={() => handleDownload(selectedAsset)}
                >
                  {downloadingId === selectedAsset.id ? <Spinner size="sm" /> : t('assets.download')}
                </Button>
              </div>
            ) : null}
          </main>

          <aside className="asset-browser__filters" aria-label="Asset filters">
            <div className="asset-filter__top">
              <h2>Filter</h2>
              <button
                type="button"
                onClick={() => {
                  setFileTypes(new Set());
                  setOrientations(new Set());
                }}
                disabled={fileTypes.size === 0 && orientations.size === 0}
              >
                Clear all
              </button>
            </div>

            <fieldset className="asset-filter__group">
              <legend>File type</legend>
              <div className="asset-filter__checks asset-filter__checks--types">
                {FILE_TYPES.map(({ value, label }) => (
                  <label key={value}>
                    <input
                      type="checkbox"
                      checked={fileTypes.has(value)}
                      onChange={() => toggleFilter(value, setFileTypes)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="asset-filter__group">
              <legend>Orientation</legend>
              <div className="asset-filter__checks">
                {ORIENTATIONS.map(({ value, label }) => (
                  <label key={value}>
                    <input
                      type="checkbox"
                      checked={orientations.has(value)}
                      onChange={() => toggleFilter(value, setOrientations)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </aside>
        </div>
      ) : null}
    </section>
  );
}
